// Telif arastirmasi: USPTO giyim sinifi (IC 025) taramasi + derin inceleme (terim terim
// telif/kamu mali ve marka durumu). Her bulgunun resmi USPTO kayit (TSDR) baglantisi ve
// elle dogrulama icin sorgu metni saklanir.

import { useEffect, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { ExternalLink, Loader2, RefreshCw, Search, Microscope, Copy, Check } from "lucide-react";
import { authHeaders, getBackendUrl } from "../../lib/http";

type Level = "high" | "warn" | "info";
interface Mark { serial: string; wordmark: string; owner: string; registered: boolean; registrationType: string; status: string; classes: string[]; disclaimer?: string; url?: string; match?: string; ic025?: boolean }
interface Hit { phrase: string; source: "title" | "tag" | "print" | "term"; level: Level; marks: Mark[]; query: string; searchPage: string }
interface CopyrightReg { title: string; regNo: string; regClass: string; claimant: string; date: string; url: string }
interface CopyrightHit { phrase: string; source: "title" | "print" | "term"; level: "high" | "warn"; regs: CopyrightReg[] }
export interface UsptoCheck { status: "running" | "ready" | "failed"; error?: string; at: string; checked: number; hits: Hit[]; copyrightHits?: CopyrightHit[] }
interface DeepTerm { term: string; kind: string; verdict: "avoid" | "caution" | "ok"; copyrightTr: string; trademarkTr: string; canUseTr: string[]; avoidTr: string[]; marks: Mark[]; query: string; searchPage: string }
export interface DeepResearch { status: "running" | "ready" | "failed"; error?: string; at: string; overallTr?: string; terms?: DeepTerm[] }

const LEVEL: Record<Level, [string, string]> = {
  high: ["Tescilli marka", "bg-red-100 text-red-800"],
  warn: ["Dikkat", "bg-amber-100 text-amber-800"],
  info: ["Bilgi", "bg-gray-100 text-gray-600"],
};
const SOURCE: Record<Hit["source"], string> = { title: "Başlık", tag: "Etiket", print: "Tasarımdaki yazı", term: "Riskli terim" };
const VERDICT: Record<DeepTerm["verdict"], [string, string]> = {
  avoid: ["Kullanma", "bg-red-100 text-red-800"], caution: ["Sınırlı kullan", "bg-amber-100 text-amber-800"], ok: ["Kullanılabilir", "bg-emerald-100 text-emerald-800"],
};
const fresh = (at: string) => Date.now() - Date.parse(at) < 5 * 60_000;

async function post(path: string) {
  const r = await fetch(`${getBackendUrl()}/api/analyses${path}`, { method: "POST", headers: authHeaders() });
  const j = await r.json().catch(() => null);
  if (!r.ok) throw new Error(j?.error ?? `İstek başarısız (${r.status})`);
}

function CopyQuery({ q }: { q: string }) {
  const [ok, setOk] = useState(false);
  return (
    <button onClick={() => { void navigator.clipboard.writeText(q); setOk(true); setTimeout(() => setOk(false), 1200); }}
      title={`Sorguyu kopyala: ${q}`} className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-indigo-600">
      {ok ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />} sorgu
    </button>
  );
}

function MarkLinks({ marks }: { marks: Mark[] }) {
  // Ayni marka adini bir kez goster (birden cok kayit -> ilk kaydin baglantisi + sayi)
  const by = new Map<string, Mark[]>();
  for (const m of marks) by.set(`${m.wordmark}|${m.owner}`, [...(by.get(`${m.wordmark}|${m.owner}`) ?? []), m]);
  return (
    <span className="flex flex-wrap gap-x-3 gap-y-0.5">
      {[...by.values()].map((ms) => {
        const m = ms[0];
        return (
          <a key={m.serial} href={m.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs text-indigo-600 hover:underline"
            title={`${m.registered ? `Tescilli (${m.registrationType})` : m.status} · ${m.classes.join(", ")}${m.disclaimer ? ` · disclaimer: ${m.disclaimer}` : ""}`}>
            {m.wordmark} · {m.owner}{ms.length > 1 ? ` (${ms.length} kayıt)` : ""} <ExternalLink className="h-3 w-3" />
          </a>
        );
      })}
    </span>
  );
}

export function CopyrightResearchPanel({ analysisId, uspto, deep, onChanged }: {
  analysisId: string; uspto: UsptoCheck | null; deep: DeepResearch | null; onChanged: () => void;
}) {
  const running = (uspto?.status === "running" && fresh(uspto.at)) || (deep?.status === "running" && fresh(deep.at));
  useEffect(() => {
    if (!running) return;
    const t = setInterval(onChanged, 4000);
    return () => clearInterval(t);
  }, [running]);
  const scan = useMutation({ mutationFn: () => post(`/${analysisId}/uspto-check`), onSuccess: onChanged });
  const dive = useMutation({ mutationFn: () => post(`/${analysisId}/copyright-deep`), onSuccess: onChanged });
  const [showInfo, setShowInfo] = useState(false);
  const hits = uspto?.hits ?? [];
  const shown = hits.filter((h) => showInfo || h.level !== "info");
  const usptoRunning = uspto?.status === "running" && fresh(uspto.at);
  const deepRunning = deep?.status === "running" && fresh(deep.at);

  return (
    <div className="mt-4 space-y-3">
      {/* USPTO taramasi */}
      <div className="rounded-lg border border-gray-200 p-3">
        <div className="flex flex-wrap items-center gap-2">
          <Search className="h-4 w-4 text-indigo-500" />
          <p className="text-sm font-semibold text-gray-900">USPTO marka + Telif Ofisi taraması</p>
          {uspto?.status === "ready" && <span className="text-xs text-gray-500">{uspto.checked} ifade tarandı · {new Date(uspto.at).toLocaleDateString("tr-TR")}</span>}
          <button onClick={() => scan.mutate()} disabled={scan.isPending || usptoRunning} className="ml-auto inline-flex items-center gap-1 text-xs text-gray-500 hover:text-indigo-600 disabled:opacity-50">
            {usptoRunning || scan.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />} {uspto ? "Yeniden tara" : "Tara"}
          </button>
        </div>
        <p className="mt-1 text-xs text-gray-500">Başlık bölümleri, etiketler, tasarımdaki yazılar ve riskli terimler USPTO'da canlı giyim markalarıyla karşılaştırılır. Yalnız kelime markalarını bulur; logo ve çizim telifi bu taramada yoktur.</p>
        {usptoRunning && <p className="mt-2 flex items-center gap-2 text-sm text-gray-600"><Loader2 className="h-4 w-4 animate-spin" /> USPTO taranıyor (ifade başına ~1 sn)…</p>}
        {uspto?.status === "failed" && <p className="mt-2 text-sm text-red-600">{uspto.error ?? "Taranamadı."}</p>}
        {uspto?.status === "ready" && (
          shown.length ? (
            <ul className="mt-2 divide-y divide-gray-100">
              {shown.map((h) => (
                <li key={h.source + h.phrase} className="py-1.5">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${LEVEL[h.level][1]}`}>{LEVEL[h.level][0]}</span>
                    <span className="text-xs text-gray-500">{SOURCE[h.source]}</span>
                    <span className="text-sm text-gray-900">{h.phrase}</span>
                    <CopyQuery q={h.query} />
                    <a href={h.searchPage} target="_blank" rel="noopener noreferrer" className="text-xs text-gray-500 hover:text-indigo-600 hover:underline">USPTO arama</a>
                  </div>
                  <div className="mt-0.5 pl-1"><MarkLinks marks={h.marks} /></div>
                </li>
              ))}
            </ul>
          ) : <p className="mt-2 text-sm text-emerald-700">Giyim sınıfında eşleşen tescilli marka bulunmadı.</p>
        )}
        {uspto?.status === "ready" && hits.some((h) => h.level === "info") && (
          <button onClick={() => setShowInfo((v) => !v)} className="mt-1 text-xs text-gray-500 hover:underline">
            {showInfo ? "Bilgi düzeyindekileri gizle" : `Bilgi düzeyinde ${hits.filter((h) => h.level === "info").length} eşleşme daha (tek kelime, sahibi hak iddia etmeyen)`}
          </button>
        )}
        {scan.error && <p className="mt-1 text-xs text-red-600">{(scan.error as Error).message}</p>}

        {/* ABD Telif Ofisi: tescilli gorsel eser basligi (ayni tarama) */}
        {uspto?.status === "ready" && uspto.copyrightHits && (
          <div className="mt-3 border-t border-gray-100 pt-2">
            <p className="text-xs font-semibold text-gray-700">Telif Ofisi · tescilli görsel eser başlığı</p>
            {uspto.copyrightHits.length ? (
              <ul className="mt-1 divide-y divide-gray-100">
                {uspto.copyrightHits.map((h) => (
                  <li key={h.source + h.phrase} className="py-1.5">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${h.level === "high" ? "bg-red-100 text-red-800" : "bg-amber-100 text-amber-800"}`}>{h.level === "high" ? "Tescilli eser" : "Dikkat"}</span>
                      <span className="text-xs text-gray-500">{SOURCE[h.source]}</span>
                      <span className="text-sm text-gray-900">{h.phrase}</span>
                    </div>
                    <span className="mt-0.5 flex flex-wrap gap-x-3 gap-y-0.5 pl-1">
                      {h.regs.map((r) => (
                        <a key={r.regNo} href={r.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs text-indigo-600 hover:underline">
                          {r.title} · {r.regNo} · {r.claimant}{r.date ? ` · ${new Date(r.date).toLocaleDateString("tr-TR")}` : ""} <ExternalLink className="h-3 w-3" />
                        </a>
                      ))}
                    </span>
                  </li>
                ))}
              </ul>
            ) : <p className="mt-1 text-sm text-emerald-700">Tescilli görsel eser başlığıyla eşleşme bulunmadı.</p>}
            <p className="mt-1 text-[11px] text-gray-500">Tasarımdaki yazılar ve başlık bölümleri ABD Telif Ofisi kayıtlarında aranır. Aynı başlığı 3+ kişi tescil ettirdiyse yaygın ifade sayılır; çizimin kendisi bu taramada karşılaştırılmaz.</p>
          </div>
        )}
      </div>

      {/* Derin inceleme */}
      <div className="rounded-lg border border-gray-200 p-3">
        <div className="flex flex-wrap items-center gap-2">
          <Microscope className="h-4 w-4 text-indigo-500" />
          <p className="text-sm font-semibold text-gray-900">Derin inceleme · terim terim telif ve marka</p>
          <button onClick={() => dive.mutate()} disabled={dive.isPending || deepRunning} className="ml-auto inline-flex items-center gap-1 rounded-lg border border-gray-300 bg-white px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50">
            {deepRunning || dive.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Microscope className="h-3.5 w-3.5" />} {deep?.status === "ready" ? "Yeniden incele" : "Derin inceleme yap"}
          </button>
        </div>
        <p className="mt-1 text-xs text-gray-500">Her riskli karakter, marka ve isim için ayrı ayrı: telif ve kamu malı durumu (ilk yayın yılı, hangi sürüm serbest), USPTO'daki canlı kayıtlar, ne yapabilirsin ve neden kaçınmalısın. İsteğe bağlıdır (yaklaşık 1 dakika, birkaç sent). Sonra telif çözümü üretirsen bu bulguları kullanır.</p>
        {deepRunning && <p className="mt-2 flex items-center gap-2 text-sm text-gray-600"><Loader2 className="h-4 w-4 animate-spin" /> İnceleniyor (yaklaşık 1 dakika)…</p>}
        {deep?.status === "failed" && <p className="mt-2 text-sm text-red-600">{deep.error ?? "İncelenemedi."}</p>}
        {deep?.status === "ready" && (
          <div className="mt-2 space-y-2">
            {deep.overallTr && <p className="text-sm text-gray-700">{deep.overallTr}</p>}
            {(deep.terms ?? []).map((t) => (
              <div key={t.term} className="rounded-lg bg-gray-50 p-2.5">
                <div className="flex flex-wrap items-center gap-2">
                  <p className="text-sm font-semibold text-gray-900">{t.term}</p>
                  <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${VERDICT[t.verdict][1]}`}>{VERDICT[t.verdict][0]}</span>
                  <CopyQuery q={t.query} />
                  <a href={t.searchPage} target="_blank" rel="noopener noreferrer" className="text-xs text-gray-500 hover:text-indigo-600 hover:underline">USPTO arama</a>
                </div>
                <p className="mt-1 text-xs text-gray-700"><span className="font-medium">Telif:</span> {t.copyrightTr}</p>
                <p className="mt-0.5 text-xs text-gray-700"><span className="font-medium">Marka:</span> {t.trademarkTr}</p>
                <div className="mt-1 grid gap-2 sm:grid-cols-2">
                  {t.canUseTr.length > 0 && <ul className="list-disc space-y-0.5 pl-4 text-xs text-emerald-800">{t.canUseTr.map((x, i) => <li key={i}>{x}</li>)}</ul>}
                  {t.avoidTr.length > 0 && <ul className="list-disc space-y-0.5 pl-4 text-xs text-red-800">{t.avoidTr.map((x, i) => <li key={i}>{x}</li>)}</ul>}
                </div>
                {t.marks.length > 0 && <div className="mt-1"><MarkLinks marks={t.marks} /></div>}
              </div>
            ))}
          </div>
        )}
        {dive.error && <p className="mt-1 text-xs text-red-600">{(dive.error as Error).message}</p>}
      </div>
    </div>
  );
}
